import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { DealerDotDto } from '../models/dealer-dot.model';
import { ApiContract } from '../models/framework.model';
import { EnvironmentConfig } from '../models/config.model';
import { ConfigService } from './config.service';
import { ErrorService } from './error.service';
import { HttpErrorResponse } from '@angular/common/http';

/**
 * Service for retrieving Dedicated Originations Team (DOT) information
 * from the ACAdvantage shared API.
 */
@Injectable({
  providedIn: 'root'
})
export class DealerDotService {
  private readonly _http = inject(HttpClient);
  private readonly _errorService = inject(ErrorService);

  private readonly _baseApiUrl: string | undefined;

  constructor() {
    const config: EnvironmentConfig = inject(ConfigService);
    // Normalise trailing slashes so URL composition is always well-formed.
    this._baseApiUrl = config.sharedApiUrl?.replace(/\/+$/, '');
  }

  /**
   * Retrieves the DOT information for the given dealer number.
   * Returns null when no DOT record exists for the dealer or on error.
   * @param dealerNumber The dealer number to look up.
   */
  getDealerDot(dealerNumber: number): Observable<DealerDotDto | null> {
    if (!this._baseApiUrl) {
      this._errorService.logError(
        'getDealerDot',
        new Error('DealerDotService: sharedApiUrl is not configured. DOT lookup cannot proceed.')
      );
      return of(null);
    }

    const url = `${this._baseApiUrl}/dealer/dot/${dealerNumber}`;
    return this._http
      .get<ApiContract<DealerDotDto | null>>(url)
      .pipe(
        map(response => {
          if (!response?.isSuccessful) {
            this._errorService.logError(
              'getDealerDot',
              new Error(`DOT lookup failed for dealer ${dealerNumber}: ${response?.statusMessage ?? 'unknown error'}`)
            );
            return null;
          }
          return response.payload ?? null;
        }),
        catchError((error: HttpErrorResponse) => {
          this._errorService.logError('getDealerDot', error);
          return of(null);
        })
      );
  }
}
